import { Dimensions, type ScaledSize } from "react-native";
import { useSyncExternalStore } from "react";

function getWindowDimensions(): ScaledSize {
  return Dimensions.get("window");
}

/**
 * The window a web server render sees: React Native Web has no viewport there and reports 0×0.
 * The hydrating client render must read the same value, or every window-sized style mismatches
 * the server HTML, and React never patches attribute mismatches (tiles stayed 1px wide). React
 * re-renders with the real window right after hydrating. Native never hydrates, so it never reads
 * this snapshot.
 */
const SERVER_WINDOW_DIMENSIONS: ScaledSize = { fontScale: 1, height: 0, scale: 1, width: 0 };

function getServerWindowDimensions(): ScaledSize {
  return SERVER_WINDOW_DIMENSIONS;
}

function subscribeWindowDimensions(onChange: () => void) {
  const subscription = Dimensions.addEventListener("change", onChange);
  return () => subscription.remove();
}

/**
 * Rotation is one external-store update, shared by every layout reader. RN's state/effect hook
 * keeps a separate snapshot per component, so a parent can change the rail while a child still
 * renders its old toolbar/card geometry. Read the current dimensions during every render and
 * make dimension events synchronous, including when they interrupt a concurrent transition.
 */
export function useWindowDimensions(): ScaledSize {
  return useSyncExternalStore(
    subscribeWindowDimensions,
    getWindowDimensions,
    getServerWindowDimensions,
  );
}
